import {
  LdtStatusFilterValue,
  createOption,
} from '@ldt/features/orders/statuses/workflow_shared';
import { OrderStatus } from 'src/types';
import type { OrderStatusFilterOption } from 'src/types/status_filter';
import {
  isClinicAdmin,
  isCustomerSuccessOrSalesUser,
  isOrderingPhysician,
} from 'src/utils';

/** CS/Sales "Processing" filter statuses. */
const CS_SALES_PROCESSING_STATUSES: OrderStatus[] = [
  OrderStatus.AiProcessing,
  OrderStatus.AiError,
  OrderStatus.ReportPending,
  OrderStatus.ReportFaxPending,
  OrderStatus.ReportFaxFailed,
  OrderStatus.PathologyFaxPending,
  OrderStatus.PathologyFaxFailed,
  OrderStatus.ReportRejected,
  OrderStatus.LabProcessing,
  OrderStatus.LabError,
  OrderStatus.SlideImageProcessing,
  OrderStatus.SlideImageQualityControlPending,
  OrderStatus.SlideImageQualityControlFailed,
];

/** Ordering Physician / Clinic Admin "Processing" filter statuses. */
const OP_CA_PROCESSING_STATUSES: OrderStatus[] = [
  OrderStatus.QualityControlApproved,
  OrderStatus.PathologyFaxPending,
  OrderStatus.PathologyFaxFailed,
  OrderStatus.LabProcessing,
  OrderStatus.LabError,
  OrderStatus.AiProcessing,
  OrderStatus.AiError,
  OrderStatus.ReportPending,
  OrderStatus.ReportRejected,
  OrderStatus.SlideImageProcessing,
  OrderStatus.SlideImageQualityControlPending,
  OrderStatus.SlideImageQualityControlFailed,
];

export function getStatusFilterOptions(): Map<string, OrderStatusFilterOption> {
  const isCSOrSalesUser = isCustomerSuccessOrSalesUser();
  const isOrderingPhysicianOrClinicAdmin =
    isOrderingPhysician() || isClinicAdmin();

  if (isCSOrSalesUser) {
    return new Map<string, OrderStatusFilterOption>([
      createOption(LdtStatusFilterValue.ALL_ORDERS, 'All Orders'),
      createOption(LdtStatusFilterValue.REPORT_READY, 'Report Ready', [
        OrderStatus.ReportReady,
        OrderStatus.ReportFaxed,
      ]),
      createOption(
        LdtStatusFilterValue.PROCESSING,
        'Processing',
        CS_SALES_PROCESSING_STATUSES,
      ),
      createOption(LdtStatusFilterValue.QC_APPROVED, 'QC Approved', [
        OrderStatus.QualityControlApproved,
      ]),
      createOption(LdtStatusFilterValue.QC_PENDING, 'QC Pending', [
        OrderStatus.QualityControlPending,
      ]),
      createOption(LdtStatusFilterValue.DRAFTED, 'Drafts', [OrderStatus.Draft]),
    ]);
  }

  if (isOrderingPhysicianOrClinicAdmin) {
    return new Map<string, OrderStatusFilterOption>([
      createOption(LdtStatusFilterValue.ALL_ORDERS, 'All Orders'),
      createOption(LdtStatusFilterValue.REPORT_READY, 'Report Released', [
        OrderStatus.ReportReady,
      ]),
      createOption(
        LdtStatusFilterValue.REPORT_FAX_PENDING,
        'Report Fax Pending',
        [OrderStatus.ReportFaxFailed, OrderStatus.ReportFaxPending],
      ),
      createOption(LdtStatusFilterValue.REPORT_FAXED, 'Report Faxed', [
        OrderStatus.ReportFaxed,
      ]),
      createOption(
        LdtStatusFilterValue.PROCESSING,
        'Processing',
        OP_CA_PROCESSING_STATUSES,
      ),
      createOption(LdtStatusFilterValue.QC_PENDING, 'Order Created', [
        OrderStatus.QualityControlPending,
      ]),
      createOption(LdtStatusFilterValue.DRAFTED, 'Drafts', [OrderStatus.Draft]),
    ]);
  }

  throw new Error('Invalid user role');
}
